import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface MapState {
  longitude: number;
  latitude: number;
  zoom: number;
  satelliteView: boolean;
  poiReloadTrigger: number;
}

const getInitialState = (): MapState => {
  const urlParams = new URLSearchParams(window.location.search);

  const longitude = urlParams.get('lon');
  const latitude = urlParams.get('lat');
  const zoom = urlParams.get('zoom');

  const storedState = localStorage.getItem('mapState');

  const parseNumber = (value: string | null): number | null => {
    if (value === null) return null;
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const parsedLongitude = parseNumber(longitude);
  const parsedLatitude = parseNumber(latitude);
  const parsedZoom = parseNumber(zoom);

  if (
    parsedLongitude !== null &&
    parsedLatitude !== null &&
    parsedZoom !== null
  ) {
    let satelliteView = false;
    if (storedState) {
      try {
        const parsedState = JSON.parse(storedState) as MapState;
        satelliteView = parsedState.satelliteView;
      } catch {
        console.error('Error parsing map state from local storage.');
      }
    }

    return {
      longitude: parsedLongitude,
      latitude: parsedLatitude,
      zoom: parsedZoom,
      satelliteView: satelliteView,
      poiReloadTrigger: 0,
    };
  }

  if (storedState) {
    try {
      const parsedState = JSON.parse(storedState) as MapState;
      if (
        Number.isFinite(parsedState.longitude) &&
        Number.isFinite(parsedState.latitude) &&
        Number.isFinite(parsedState.zoom)
      ) {
        return parsedState;
      }
    } catch {
      console.error('Error parsing map state from local storage.');
    }
  }

  const defaultState: MapState = {
    // default spot when map loads for the first time, currently set to Rapperswil
    longitude: 8.8164042,
    latitude: 47.2266532,
    zoom: 14,
    satelliteView: false,
    poiReloadTrigger: 0,
  };

  navigator.geolocation.getCurrentPosition(
    (position) => {
      const { longitude, latitude } = position.coords;
      localStorage.setItem(
        'mapState',
        JSON.stringify({ ...defaultState, longitude, latitude })
      );
    },
    (error) => console.error('Geolocation error:', error)
  );

  return defaultState;
};

const initialState: MapState = getInitialState();

const mapSlice = createSlice({
  name: 'map',
  initialState,
  reducers: {
    setPosition(
      state,
      action: PayloadAction<{
        longitude: number;
        latitude: number;
        zoom?: number;
      }>
    ) {
      state.longitude = action.payload.longitude;
      state.latitude = action.payload.latitude;
      if (action.payload.zoom !== undefined) {
        state.zoom = action.payload.zoom;
      }

      localStorage.setItem('mapState', JSON.stringify(state));

      const url = new URL(window.location.href);
      url.searchParams.set('lon', state.longitude.toFixed(6));
      url.searchParams.set('lat', state.latitude.toFixed(6));
      url.searchParams.set('zoom', state.zoom.toFixed(2));
    },
    setZoom(state, action: PayloadAction<number>) {
      state.zoom = action.payload;
      localStorage.setItem('mapState', JSON.stringify(state));
    },
    toggleSatelliteView(state) {
      state.satelliteView = !state.satelliteView;
      localStorage.setItem('mapState', JSON.stringify(state));
    },
    triggerPoiReload(state) {
      state.poiReloadTrigger += 1;
    },
  },
});

export const { setPosition, setZoom, toggleSatelliteView, triggerPoiReload } =
  mapSlice.actions;
export default mapSlice.reducer;
